import { useEffect, useState } from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { LoadRefPillInput } from 'components/input/LoadRefPillInput';
import { RHFCustomerSelect } from 'components/input/RHFCustomerSelect';
import {
  InputValue,
  normalizeFieldAttributePath,
} from 'components/input/RHFTextInput';
import { useExtendedFormContext } from 'contexts/extendedFormContext';
import { useFieldAttributes } from 'hooks/useLoadContext';
import useTMSContext from 'hooks/useTMSContext';
import { LoadTextInput } from 'pages/LoadView/LoadInformation/Components';
import {
  NormalizedLoad,
  TMSCustomer,
  createInitCompanyCoreInfo,
} from 'types/Load';
import { getFieldAttribute, initFieldAttributes } from 'types/LoadAttributes';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { emailFieldOptions } from 'utils/formValidators';

type CustomerSectionFormProps = {
  formMethods: UseFormReturn<NormalizedLoad>;
  customers: Maybe<TMSCustomer[]>;
  setCustomers: React.Dispatch<React.SetStateAction<Maybe<TMSCustomer[]>>>;
  isLoadingCustomers: boolean;
  setIsLoadingCustomers: React.Dispatch<React.SetStateAction<boolean>>;
  originalSuggestionData?: NormalizedLoad;
};

export function CustomerSectionForm({
  formMethods,
  customers,
  setCustomers,
  isLoadingCustomers,
  setIsLoadingCustomers,
  originalSuggestionData,
}: CustomerSectionFormProps) {
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = formMethods;

  const { tmsName, tmsID } = useTMSContext();
  const { aiDefaultValues } = useExtendedFormContext();
  const allFieldAttrs = useFieldAttributes();
  const [isMcleodTMS, setIsMcleodTMS] = useState<boolean>(
    tmsName === TMS.McleodEnterprise
  );

  const watchedCustomerID = watch('customer.externalTMSID');
  const refNumberAttr =
    getFieldAttribute(
      allFieldAttrs,
      normalizeFieldAttributePath('customer.refNumber')
    ) ?? initFieldAttributes;
  const poNumsAttr =
    getFieldAttribute(allFieldAttrs, normalizeFieldAttributePath('poNums')) ??
    initFieldAttributes;

  useEffect(() => {
    if (isMcleodTMS) {
      setIsMcleodTMS(true);
    }
  }, [tmsName]);

  useEffect(() => {
    if (watchedCustomerID) {
      const selectedCustomer = customers?.find(
        (c) => c.externalTMSID === watchedCustomerID
      );
      if (!selectedCustomer) {
        return;
      }

      // Autofill customer object
      Object.entries(selectedCustomer).forEach(([key, value]) => {
        if (key in createInitCompanyCoreInfo()) {
          setValue(`customer.${key}` as FieldPath<NormalizedLoad>, value, {
            shouldDirty: aiDefaultValues,
          });
        }
      });
    }
  }, [watchedCustomerID, customers, aiDefaultValues, setValue]);

  return (
    <>
      {!isMcleodTMS ? (
        <LoadTextInput name='customer.name' label='Name' />
      ) : (
        <RHFCustomerSelect
          required={true}
          name={`customer.externalTMSID`}
          label='Name'
          control={control}
          errors={errors}
          customers={customers}
          setCustomers={setCustomers}
          isLoadingCustomers={isLoadingCustomers}
          setIsLoadingCustomers={setIsLoadingCustomers}
          tmsID={tmsID}
        />
      )}

      <LoadTextInput name='customer.addressLine1' label='Address Line 1' />

      <LoadTextInput name='customer.addressLine2' label='Address Line 2' />
      <LoadTextInput name='customer.city' label='City' />
      <LoadTextInput name='customer.state' label='State' />
      <LoadTextInput
        name='customer.zipCode'
        label='Zip Code'
        placeholder='12345'
        // Zipcode validation disabled to prevent false positives due to Canada postal and read-only values from TMS,
        // options={zipCodeFieldOptions}
      />
      <LoadTextInput name='customer.country' label='Country' />
      <LoadTextInput name='customer.contact' label='Contact' />
      <LoadTextInput
        name='customer.phone'
        label='Phone'
        placeholder='(555) 555-5555'
        // options={phoneFieldOptions}
        inputValue={InputValue.PHONE_NUMBER}
      />
      <LoadTextInput
        name='customer.email'
        label='Email'
        placeholder='example@email.com'
        options={emailFieldOptions}
      />
      {!refNumberAttr.isNotSupported && (
        <LoadRefPillInput
          name='customer.refNumber'
          label='Ref # / BOL'
          control={control}
          originalSuggestionData={originalSuggestionData || getValues()}
          autoSelectFirstOption={false}
          disabled={refNumberAttr.isReadOnly}
          pillDisplayContext='loadView'
          highlightNewValuesInLoadView={true}
        />
      )}

      {!poNumsAttr.isNotSupported && (
        <LoadRefPillInput
          name='poNums'
          label='PO #'
          control={control}
          originalSuggestionData={originalSuggestionData || getValues()}
          autoSelectFirstOption={false}
          disabled={poNumsAttr.isReadOnly}
          pillDisplayContext='loadView'
          highlightNewValuesInLoadView={true}
        />
      )}
    </>
  );
}
